import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Plus, Search, Download, MoreHorizontal, Ban, Undo2, ExternalLink, Loader2, ReceiptText, Mail, StickyNote, X } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "../components/DashboardLayout";
import { BasarNav } from "../components/basar/BasarNav";
import { ManualSaleDialog } from "../components/basar/ManualSaleDialog";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Alert, AlertDescription } from "../components/ui/alert";
import { api, errorMessage, formatDateTime } from "../lib/api";
import { basarOrderStatusMeta, basarOrderUrl, basarPaymentLabel, compressNumbers, nok, useMyBasar } from "../lib/basar";
import { useLang } from "../context/LanguageContext";

const FILTERS = [
  { key: "", label: "Alle" },
  { key: "paid", label: "Betalte" },
  { key: "pending_payment", label: "Venter på Vipps" },
  { key: "cancelled", label: "Kansellerte" },
  { key: "refunded", label: "Refunderte" },
];

// /dashboard/basar/:id/sal – alle loddsal (nett + manuelt), søk på namn/mobil/loddnummer, manuelt sal, kanseller/refunder, CSV
export default function BasarSalesPage() {
  const { t } = useLang();
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { basar, emailEnabled, loading: basarLoading, error, reload } = useMyBasar(id);
  const status = params.get("status") || "";
  const [q, setQ] = useState(params.get("q") || "");
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [manualOpen, setManualOpen] = useState(params.get("ny") === "1");
  const [confirm, setConfirm] = useState(null); // { order }
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (error === "notfound") navigate("/dashboard/basar");
  }, [error, navigate]);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/my/basar/${id}/orders`, { params: { q, status } }).then((res) => setOrders(res.data.orders)).catch(() => setOrders([])).finally(() => setLoading(false));
  }, [id, q, status]);

  useEffect(() => {
    const h = setTimeout(load, q ? 250 : 0);
    return () => clearTimeout(h);
  }, [load, q]);

  const setStatusFilter = (key) => {
    const next = new URLSearchParams(params);
    if (key) next.set("status", key);
    else next.delete("status");
    next.delete("ny");
    setParams(next);
  };

  const afterChange = () => {
    load();
    reload();
  };

  const cancel = async () => {
    const order = confirm?.order;
    if (!order) return;
    setConfirm(null);
    setBusy(order.id);
    try {
      await api.post(`/my/basar/${id}/orders/${order.id}/cancel`, { reason });
      toast.success(order.status === "paid" ? t("Salget er angret og numrene er frigitt") : t("Bestillingen er kansellert"));
      setReason("");
      afterChange();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy("");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get(`/my/basar/${id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "basar-lodd.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const canSell = basar && ["open", "closed"].includes(basar.status) && !basar.draw_published_at;
  const canUndo = (o) => basar && !basar.draw_published_at && ["paid", "pending_payment", "processing"].includes(o.status);

  const sourceLabel = (o) => (o.source === "manual" ? t("Manuelt") : t("Nett"));

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="basar-sales-page">
        <BasarNav
          basar={basar}
          actions={basar && (
            <>
              <Button variant="outline" onClick={exportCsv} data-testid="basar-sales-export-button"><Download className="w-4 h-4 mr-1.5" /> <span className="hidden sm:inline">{t("Eksporter CSV")}</span><span className="sm:hidden">CSV</span></Button>
              <Button onClick={() => setManualOpen(true)} disabled={!canSell} title={!canSell ? t("Åpne basaren før du registrerer salg") : undefined} data-testid="basar-manual-sale-button"><Plus className="w-4 h-4 mr-1.5" /> {t("Registrer salg")}</Button>
            </>
          )}
        />

        {basar && basar.status === "draft" && (
          <Alert data-testid="basar-sales-draft-note"><AlertDescription>{t("Basaren er en kladd. Åpne loddsalget fra oversikten før du registrerer salg.")}</AlertDescription></Alert>
        )}

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-1.5" data-testid="basar-sales-filters">
            {FILTERS.map((f) => (
              <Button key={f.key} variant={status === f.key ? "default" : "outline"} size="sm" onClick={() => setStatusFilter(f.key)} data-testid={`basar-sales-filter-${f.key || "all"}`}>{t(f.label)}</Button>
            ))}
          </div>
          <div className="relative sm:w-80">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Søk på navn, mobil, loddnummer eller ordre")} className="pl-9 pr-8" data-testid="basar-sales-search-input" />
            {q && <button type="button" onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label={t("Nullstill søk")} data-testid="basar-sales-search-clear"><X className="w-4 h-4" /></button>}
          </div>
        </div>

        {loading || basarLoading ? (
          <div className="space-y-2" data-testid="basar-loading-state">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 rounded-lg" />)}</div>
        ) : !orders?.length ? (
          <Card className="border-dashed border-border" data-testid="basar-empty-state">
            <CardContent className="p-10 text-center space-y-3">
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]"><ReceiptText className="w-6 h-6" /></span>
              <p className="font-heading font-semibold">{q || status ? t("Ingen salg passet søket") : t("Ingen salg ennå")}</p>
              <p className="text-sm text-muted-foreground">{q || status ? t("Prøv et annet søkeord eller filter.") : t("Nettsalg dukker opp her automatisk. Salg ved døren registrerer du med knappen «Registrer salg».")}</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Desktop-tabell */}
            <Card className="border-border hidden md:block">
              <Table data-testid="basar-sales-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("Ordre")}</TableHead>
                    <TableHead>{t("Kjøper")}</TableHead>
                    <TableHead>{t("Loddnummer")}</TableHead>
                    <TableHead className="text-right">{t("Beløp")}</TableHead>
                    <TableHead>{t("Betaling")}</TableHead>
                    <TableHead>{t("Status")}</TableHead>
                    <TableHead className="w-12"><span className="sr-only">{t("Handlinger")}</span></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((o) => {
                    const st = basarOrderStatusMeta(o);
                    return (
                      <TableRow key={o.id} data-testid="basar-sales-row" data-order-id={o.id} data-status={o.status}>
                        <TableCell className="align-top">
                          <p className="font-medium tabular-nums">{o.order_number}</p>
                          <p className="text-xs text-muted-foreground whitespace-nowrap">{formatDateTime(o.created_at)}</p>
                        </TableCell>
                        <TableCell className="align-top">
                          <p className="font-medium">{o.name}</p>
                          <p className="text-xs text-muted-foreground tabular-nums">{o.phone}{o.email ? ` · ${o.email}` : ""}</p>
                          {o.note && <p className="text-xs text-muted-foreground inline-flex items-center gap-1 mt-0.5"><StickyNote className="w-3 h-3" /> {o.note}</p>}
                        </TableCell>
                        <TableCell className="align-top">
                          <p className="text-sm tabular-nums" data-testid="basar-sales-row-numbers">{compressNumbers(o.numbers)}</p>
                          <p className="text-xs text-muted-foreground">{o.quantity === 1 ? t("{n} lodd", { n: 1 }) : t("{n} lodd", { n: o.quantity })}</p>
                        </TableCell>
                        <TableCell className="align-top text-right tabular-nums font-medium">{nok(o.total_amount)}</TableCell>
                        <TableCell className="align-top">
                          <p className="text-sm">{basarPaymentLabel(o)}</p>
                          <p className="text-xs text-muted-foreground">{sourceLabel(o)}{o.email_receipt_sent_at ? <span className="inline-flex items-center gap-1 ml-1" title={t("Kvittering sendt")}><Mail className="w-3 h-3" /></span> : null}</p>
                        </TableCell>
                        <TableCell className="align-top"><Badge className={st.className} data-testid="basar-sales-row-status">{st.label}</Badge></TableCell>
                        <TableCell className="align-top">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8" disabled={busy === o.id} aria-label={t("Handlinger")} data-testid="basar-sales-row-menu">{busy === o.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreHorizontal className="w-4 h-4" />}</Button></DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem asChild><a href={basarOrderUrl(o.id)} target="_blank" rel="noreferrer" data-testid="basar-sales-row-view"><ExternalLink className="w-4 h-4 mr-2" /> {t("Se kjøperens ordreside")}</a></DropdownMenuItem>
                              {canUndo(o) && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem className="text-[hsl(var(--danger))] focus:text-[hsl(var(--danger))]" onClick={() => setConfirm({ order: o })} data-testid="basar-sales-row-cancel">
                                    {o.status === "paid" ? <Undo2 className="w-4 h-4 mr-2" /> : <Ban className="w-4 h-4 mr-2" />} {o.status === "paid" ? (o.payment_method === "vipps" && o.source === "web" ? t("Refunder via Vipps og frigi numrene") : t("Angre salget og frigi numrene")) : t("Kanseller reservasjonen")}
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
            {/* Mobil-kort */}
            <div className="md:hidden space-y-3" data-testid="basar-sales-cards">
              {orders.map((o) => {
                const st = basarOrderStatusMeta(o);
                return (
                  <Card key={o.id} className="border-border" data-testid="basar-sales-card" data-order-id={o.id}>
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0"><p className="font-medium">{o.name}</p><p className="text-xs text-muted-foreground tabular-nums">{o.order_number} · {formatDateTime(o.created_at)}</p></div>
                        <Badge className={`${st.className} shrink-0`}>{st.label}</Badge>
                      </div>
                      <p className="text-sm tabular-nums"><span className="text-muted-foreground">{t("Lodd")}:</span> {compressNumbers(o.numbers)}</p>
                      <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">{basarPaymentLabel(o)} · {sourceLabel(o)} · {o.phone}</span><span className="font-medium tabular-nums">{nok(o.total_amount)}</span></div>
                      <div className="flex gap-2 pt-1">
                        <Button asChild variant="outline" size="sm"><a href={basarOrderUrl(o.id)} target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4 mr-1.5" /> {t("Ordreside")}</a></Button>
                        {canUndo(o) && <Button variant="ghost" size="sm" className="text-[hsl(var(--danger))]" onClick={() => setConfirm({ order: o })} disabled={busy === o.id}>{o.status === "paid" ? t("Angre") : t("Kanseller")}</Button>}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground tabular-nums" data-testid="basar-sales-count">{orders.length === 1 ? t("{n} salg", { n: 1 }) : t("{n} salg", { n: orders.length })}</p>
          </>
        )}
      </div>

      {basar && <ManualSaleDialog basar={basar} open={manualOpen} onOpenChange={(o) => { setManualOpen(o); if (!o && params.get("ny")) { const next = new URLSearchParams(params); next.delete("ny"); setParams(next); } }} onSaved={afterChange} emailEnabled={emailEnabled} />}

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent data-testid="basar-cancel-dialog">
          <DialogHeader>
            <DialogTitle>{confirm?.order?.status === "paid" ? t("Angre salget?") : t("Kansellere reservasjonen?")}</DialogTitle>
            <DialogDescription>
              {confirm?.order?.status === "paid"
                ? confirm.order.payment_method === "vipps" && confirm.order.source === "web"
                  ? t("Beløpet ({amount}) refunderes via Vipps, og loddnumrene {numbers} blir ledige igjen.", { amount: nok(confirm.order.total_amount), numbers: compressNumbers(confirm.order.numbers) })
                  : t("Salget merkes som refundert (pengene må dere gjøre opp selv), og loddnumrene {numbers} blir ledige igjen.", { numbers: compressNumbers(confirm.order.numbers) })
                : t("Vipps-betalingen avbrytes og numrene frigis.")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="cancel-reason">{t("Begrunnelse (valgfritt)")}</Label>
            <Textarea id="cancel-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={300} data-testid="basar-cancel-reason-input" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setConfirm(null)} data-testid="basar-cancel-dialog-cancel">{t("Avbryt")}</Button>
            <Button className="bg-[hsl(var(--danger))] text-[hsl(var(--danger-foreground))] hover:opacity-95" onClick={cancel} data-testid="basar-cancel-dialog-confirm">{confirm?.order?.status === "paid" ? t("Ja, angre salget") : t("Ja, kanseller")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
